import { useAuth, useTranslation } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTrips, updateTrip, deleteTrip } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Map, Plus, Calendar, Users, ArrowRight, Archive, Trash2, MoreVertical, RotateCcw } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function Trips() {
  const { families } = useAuth();
  const { t } = useTranslation();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);

  const loadTrips = async () => {
    if (families.length > 0) {
      try {
        const r = await getTrips(families[0].family_id);
        setTrips(r.data);
      } catch (e) {}
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTrips();
  }, [families]);

  const handleArchive = async (trip, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await updateTrip(trip.trip_id, { status: 'archived' });
      toast.success('Voyage archivé');
      loadTrips();
    } catch (err) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleRestore = async (trip, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await updateTrip(trip.trip_id, { status: 'planning' });
      toast.success('Voyage restauré');
      loadTrips();
    } catch (err) {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleDeleteClick = (trip, e) => {
    e.preventDefault();
    e.stopPropagation();
    setTripToDelete(trip);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tripToDelete) return;
    try {
      await deleteTrip(tripToDelete.trip_id);
      toast.success('Voyage supprimé définitivement');
      setDeleteOpen(false);
      setTripToDelete(null);
      loadTrips();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const statusColor = {
    planning: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-gray-100 text-gray-600',
    cancelled: 'bg-red-100 text-red-600',
    archived: 'bg-slate-100 text-slate-500',
  };

  const activeTrips = trips.filter(t => t.status !== 'archived');
  const archivedTrips = trips.filter(t => t.status === 'archived');

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-40 bg-[#E2E8F0] rounded-lg animate-pulse" />
        {[1,2,3].map(i => <div key={i} className="h-32 bg-[#E2E8F0] rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  const renderTripCard = (trip, showArchiveAction = true) => {
    const daysUntil = trip.start_date ? Math.ceil((new Date(trip.start_date) - new Date()) / 86400000) : null;
    const isArchived = trip.status === 'archived';
    
    return (
      <Card key={trip.trip_id} className={`border border-[#E2E8F0]/40 rounded-2xl hover:shadow-md transition-all h-full group ${isArchived ? 'opacity-70' : ''}`} data-testid={`trip-${trip.trip_id}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <Badge className={`rounded-full text-xs border-0 ${statusColor[trip.status] || statusColor.planning}`}>
              {trip.status === 'archived' ? 'Archivé' : t(`trips.${trip.status}`)}
            </Badge>
            <div className="flex items-center gap-2">
              {daysUntil !== null && daysUntil > 0 && !isArchived && (
                <span className="text-xs text-[#3A86FF] font-medium">{daysUntil}j</span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => e.preventDefault()}>
                    <MoreVertical className="w-4 h-4 text-[#64748B]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {showArchiveAction && !isArchived ? (
                    <DropdownMenuItem onClick={(e) => handleArchive(trip, e)} className="text-amber-600">
                      <Archive className="w-4 h-4 mr-2" /> Archiver
                    </DropdownMenuItem>
                  ) : isArchived && (
                    <DropdownMenuItem onClick={(e) => handleRestore(trip, e)} className="text-blue-600">
                      <RotateCcw className="w-4 h-4 mr-2" /> Restaurer
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={(e) => handleDeleteClick(trip, e)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <Link to={`/trips/${trip.trip_id}`}>
            <h3 className="text-lg font-medium text-[#1E293B] mb-1 group-hover:text-[#3A86FF] transition-colors">
              {trip.name}
            </h3>
          </Link>
          {trip.destination && (
            <p className="text-sm text-[#64748B] mb-3">{trip.destination}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-[#64748B]">
            {trip.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(trip.start_date).toLocaleDateString()}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {trip.travelers_count || 0}
            </span>
          </div>
          <Link to={`/trips/${trip.trip_id}`}>
            <div className="mt-4 flex items-center text-xs text-[#3A86FF] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              {t('common.edit')} <ArrowRight className="w-3 h-3 ml-1" />
            </div>
          </Link>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trips-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl text-[#1E293B]">
          {t('trips.title')}
        </h1>
        <Link to="/trips/new">
          <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white px-6" data-testid="create-trip-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('trips.create')}
          </Button>
        </Link>
      </div>

      {trips.length === 0 ? (
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-16 text-center">
            <Map className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-lg font-medium text-[#1E293B] mb-2">{t('trips.noTrips')}</h3>
            <p className="text-sm text-[#64748B] mb-6">{t('trips.noTripsDesc')}</p>
            <Link to="/trips/new">
              <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white px-8" data-testid="empty-create-trip-btn">
                {t('trips.create')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="flex items-center gap-2">
              Actifs <Badge variant="secondary" className="ml-1 text-xs">{activeTrips.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <Archive className="w-4 h-4" /> Archivés <Badge variant="secondary" className="ml-1 text-xs">{archivedTrips.length}</Badge>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            {activeTrips.length === 0 ? (
              <Card className="border border-dashed border-[#E2E8F0] rounded-2xl">
                <CardContent className="p-8 text-center">
                  <p className="text-sm text-[#64748B]">Aucun voyage actif</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeTrips.map(trip => renderTripCard(trip, true))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="archived">
            {archivedTrips.length === 0 ? (
              <Card className="border border-dashed border-[#E2E8F0] rounded-2xl">
                <CardContent className="p-8 text-center">
                  <Archive className="w-10 h-10 text-[#E2E8F0] mx-auto mb-3" />
                  <p className="text-sm text-[#64748B]">Aucun voyage archivé</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedTrips.map(trip => renderTripCard(trip, false))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer le voyage</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer définitivement "{tripToDelete?.name}" ? 
              Cette action est irréversible et supprimera toutes les données associées 
              (réservations, tâches, dépenses, etc.).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} data-testid="confirm-delete-trip">
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
